import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authServices } from '@/shared/services/auth.services';
import type { User, AuthTokens, UserRole } from '@/shared/types/auth.types';

interface AuthState {
    user: User | null;
    accessToken: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    setUser: (user: User | null) => void;
    setTokens: (tokens: AuthTokens | null) => void;
    setLoading: (loading: boolean) => void;
    login: (phoneNumber: string) => Promise<void>;
    signup: (phoneNumber: string, name: string, email: string, role: 'buyer' | 'vendor' | 'admin') => Promise<void>;
    verifyOtp: (phoneNumber: string, otp: string) => Promise<void>;
    loginWithGoogle: (role?: UserRole, redirectPath?: string | null) => void;
    logout: () => Promise<void>;
    refreshTokens: () => Promise<AuthTokens | null>;
    refreshUser: () => void;
    initialize: () => Promise<void>;
}

let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
let authEventListenersRegistered = false;

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            accessToken: null,
            isAuthenticated: false,
            isLoading: true,

            setUser: (user) => set({ user, isAuthenticated: !!user }),
            setTokens: (tokens) => {
                set({
                    accessToken: tokens?.accessToken ?? null,
                    isAuthenticated: !!tokens,
                });

                if (tokens) {
                    scheduleAuthRefresh(get().refreshTokens);
                    return;
                }

                clearScheduledAuthRefresh();
            },
            setLoading: (isLoading) => set({ isLoading }),

            login: async (phoneNumber: string) => {
                await authServices.login({ phoneNumber });
            },

            signup: async (phoneNumber: string, name: string, email: string, role: 'buyer' | 'vendor' | 'admin') => {
                await authServices.signup({ phoneNumber, name, email, role });
            },

            verifyOtp: async (phoneNumber: string, otp: string) => {
                const response = await authServices.verifyOtp({ phoneNumber, otp });
                await authServices.completeOtpLogin(response);
            },

            loginWithGoogle: (role = 'buyer', redirectPath) => {
                authServices.initiateGoogleSSO(role, redirectPath);
            },

            logout: async () => {
                await authServices.logout();
                clearScheduledAuthRefresh();
                set({
                    user: null,
                    accessToken: null,
                    isAuthenticated: false,
                    isLoading: false,
                });
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('userLogout'));
                }
            },

            refreshTokens: async () => {
                const newTokens = await authServices.refreshTokens();
                if (newTokens) {
                    set({ accessToken: newTokens.accessToken });
                    scheduleAuthRefresh(get().refreshTokens);
                } else {
                    clearScheduledAuthRefresh();
                }
                return newTokens;
            },

            refreshUser: () => {
                const tokens = authServices.getTokens();
                const user = tokens
                    ? authServices.getUser() ?? authServices.getUserFromToken()
                    : null;
                set({
                    user,
                    accessToken: tokens?.accessToken ?? null,
                    isAuthenticated: !!tokens,
                });
            },

            initialize: async () => {
                if (typeof window === 'undefined') {
                    set({ isLoading: false });
                    return;
                }

                registerAuthEventListeners(set, get);
                clearScheduledAuthRefresh();
                set({ isLoading: true });

                const storedTokens = authServices.getTokens();
                if (!storedTokens) {
                    set({
                        user: null,
                        accessToken: null,
                        isAuthenticated: false,
                        isLoading: false,
                    });
                    return;
                }

                if (authServices.isPhoneAuthenticated(storedTokens) && storedTokens.refreshToken) {
                    const refreshedTokens = await get().refreshTokens();

                    if (!refreshedTokens) {
                        await authServices.handleRefreshFailure();
                        return;
                    }
                }

                const tokens = authServices.getTokens();
                const user = tokens
                    ? authServices.getUser() ?? authServices.getUserFromToken()
                    : null;

                set({
                    user,
                    accessToken: tokens?.accessToken ?? null,
                    isAuthenticated: !!tokens,
                    isLoading: false,
                });

                if (tokens) {
                    scheduleAuthRefresh(get().refreshTokens);
                }
            },
        }),
        {
            name: 'auth-storage',
            partialize: () => ({}),
        }
    )
);

function clearScheduledAuthRefresh() {
    if (refreshTimeoutId !== null) {
        clearTimeout(refreshTimeoutId);
        refreshTimeoutId = null;
    }
}

function scheduleAuthRefresh(refreshTokens: () => Promise<AuthTokens | null>) {
    clearScheduledAuthRefresh();

    const timeUntilExpiry = authServices.getTimeUntilExpiration();
    if (timeUntilExpiry === null || timeUntilExpiry <= 0) {
        return;
    }

    const refreshTime = timeUntilExpiry <= 5 * 60 * 1000
        ? 1000
        : timeUntilExpiry - 5 * 60 * 1000;

    refreshTimeoutId = setTimeout(async () => {
        const newTokens = await refreshTokens();
        if (newTokens) {
            scheduleAuthRefresh(refreshTokens);
        } else {
            clearScheduledAuthRefresh();
        }
    }, refreshTime);
}

function registerAuthEventListeners(
    set: (partial: Partial<AuthState>) => void,
    get: () => AuthState,
) {
    if (authEventListenersRegistered || typeof window === 'undefined') {
        return;
    }

    const syncAuthState = (user?: User | null, tokens?: AuthTokens | null) => {
        const nextTokens = tokens ?? authServices.getTokens();
        const nextUser = nextTokens
            ? user ?? authServices.getUser() ?? authServices.getUserFromToken()
            : null;

        set({
            user: nextUser,
            accessToken: nextTokens?.accessToken ?? null,
            isAuthenticated: !!nextTokens,
            isLoading: false,
        });

        if (nextTokens) {
            scheduleAuthRefresh(get().refreshTokens);
            return;
        }

        clearScheduledAuthRefresh();
    };

    window.addEventListener('userLogin', (event: Event) => {
        const customEvent = event as CustomEvent<{ user?: User | null; tokens?: AuthTokens | null }>;
        syncAuthState(customEvent.detail?.user, customEvent.detail?.tokens);
    });

    window.addEventListener('authTokensUpdated', (event: Event) => {
        const customEvent = event as CustomEvent<{ user?: User | null; tokens?: AuthTokens | null }>;
        syncAuthState(customEvent.detail?.user, customEvent.detail?.tokens);
    });

    window.addEventListener('userLogout', () => {
        clearScheduledAuthRefresh();
        set({
            user: null,
            accessToken: null,
            isAuthenticated: false,
            isLoading: false,
        });
    });

    authEventListenersRegistered = true;
}

if (typeof window !== 'undefined') {
    setTimeout(() => {
        void useAuthStore.getState().initialize();
    }, 0);
}
